import { type CSSProperties, globalStyle, style, styleVariants } from '@vanilla-extract/css';

import { embla } from '../components/EmblaCarousel/embla.css';

export const postsGrid = style({
  position: 'relative',
  margin: 'var(--margin)',
  width: '100%',
  gap: 'var(--gap)',

  '@media': {
    'only screen and (--mobile)': {
      gap: 'var(--mobile-gap)',
    },
  },
});

/* Carousel overrides start here */
globalStyle(`${postsGrid} ${embla} .embla`, {
  vars: {
    '--slide-control-background-color': 'var(--post-slider-control-background-color)',
    '--slide-control-text-color': 'var(--post-slider-control-text-color)',
    '--slide-control-border-color': 'var(--post-slider-control-border-color)',
  },
  '@media': {
    '(min-width: 1024px)': {
      vars: {
        '--slide-size': 'var(--desktop-slide-size)',
      },
    },
    'only screen and (--mobile)': {
      vars: {
        '--slide-control-background-color':
          'var(--mobile-slider-control-background-color, var(--post-slider-control-background-color))',
        '--slide-control-text-color': 'var(--mobile-slider-control-text-color, var(--post-slider-control-text-color))',
        '--slide-control-border-color':
          'var(--mobile-slider-control-border-color, var(--post-slider-control-border-color))',
      },
    },
  },
});

globalStyle(`${postsGrid} ${embla} .embla__viewport`, {
  paddingLeft: '0',
  paddingRight: '0',
  overflow: 'hidden',
});
/* Carousel overrides end here */

export const postSeperatorDot = style({
  backgroundColor: 'var(--separator-color)',
});

export const postSeperator = style({
  color: 'var(--separator-color)',
});

export const postCard = style({
  backgroundColor: 'var(--card-background)',
  boxShadow: 'var(--shadow)',
  ':hover': {
    boxShadow: 'var(--hover-shadow)',
  },
});

const postCardWithBackgroundImageBase = {
  backgroundColor: 'transparent',
  boxShadow: 'var(--shadow)',
  ':hover': {
    boxShadow: 'var(--hover-shadow)',
  },
} as CSSProperties;

const postCardWithBackgroundImageBeforeBase = {
  content: '""',
  position: 'absolute',
  inset: 0,
  backgroundColor: 'var(--background-image-filter-color)',
  zIndex: 1,
  height: 'inherit',
  borderRadius: 'var(--border-radius)',
} as CSSProperties;

export const postCardWithBackgroundImage = style({
  ...postCardWithBackgroundImageBase,
  '::before': {
    ...postCardWithBackgroundImageBeforeBase,
    width: '100%',
    left: '0px',
  },
});

export const postCardWithBorderAndBackgroundImage = style({
  ...postCardWithBackgroundImageBase,
  '::before': {
    ...postCardWithBackgroundImageBeforeBase,
    width: '100%',
  },
});

globalStyle(`${postCardWithBackgroundImage} > div > *`, {
  position: 'relative',
  zIndex: 2, // Ensure all children are above the filter
});

export const postCardAuthorsText = style({
  color: 'var(--authors-text-color)',
  fontFamily: 'var(--authors-font-family)',
  fontWeight: 'var(--authors-font-weight, 400)',
  zIndex: 1,
});

export const postCardTags = style({
  backgroundColor: 'var(--tag-background-color)',
  color: 'var(--tag-text-color)',
  borderRadius: 'var(--tag-border-radius)',
  textAlign: 'var(--text-align)',
  fontFamily: 'var(--tag-font-family)',
  fontWeight: 'var(--tag-font-weight, 400)',
  textTransform: 'var(--tag-text-transform)',
  zIndex: 1,
});

export const postCardContent = style({
  padding: 'var(--card-content-padding)',
});

export const postCardTitle = style({
  color: 'var(--title-text-color)',
  textAlign: 'var(--text-align)',
  fontFamily: 'var(--title-font-family)',
  fontWeight: 'var(--title-font-weight, 700)',
  zIndex: 1,
});

export const postCardSubtitle = style({
  color: 'var(--subtitle-text-color)',
  textAlign: 'var(--text-align)',
  fontFamily: 'var(--subtitle-font-family)',
  fontWeight: 'var(--subtitle-font-weight, 400)',
  zIndex: 1,
});

export const postCardTimestamp = style({
  color: 'var(--timestamps-text-color)',
  textAlign: 'var(--text-align)',
  fontWeight: 'var(--timestamps-font-weight, 400)',
  zIndex: 1,
});

export const postCardReadTime = style({
  color: 'var(--read-time-text-color)',
  textAlign: 'var(--text-align)',
});

export const postCardPremium = style({
  color: 'var(--premium-text-color)',
  background: 'var(--premium-background-color)',
  textAlign: 'var(--text-align)',
  fontFamily: 'var(--tag-font-family)',
  fontWeight: 'var(--premium-font-weight, 400)',
});

export const inputStyle = style({
  backgroundColor: 'var(--input-background-color)',
  color: 'var(--input-text-color)',
  maxWidth: 'var(--input-max-width)',
  height: '100%',

  '::placeholder': {
    color: 'var(--input-placeholder-color)',
  },
  ':focus': {
    borderColor: 'var(--input-border-focus)',
  },
});

export const inputContainer = style({
  maxWidth: 'var(--input-max-width)',
  height: 'var(--input-max-height)',
  marginBottom: 'var(--input-margin-bottom)',
});

export const inputIconFill = style({
  fill: 'var(--input-icon-fill)',
});

const xAxisDivider = style({
  position: 'relative',
  selectors: {
    '&:not(:last-child)::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      right: 0,
      width: 'var(--divider-thickness)',
      backgroundColor: 'var(--divider-color)',
      transform: 'translateX(calc(var(--gap) / 2))', // Centers the divider with the gap accounted for
    },
  },
});

const yAxisDivider = style({
  position: 'relative',
  selectors: {
    '&:not(:last-child)::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 'var(--divider-thickness)',
      backgroundColor: 'var(--divider-color)',
      transform: 'translateY(calc(var(--gap) / 2))', // Centers the divider with the gap accounted for
    },
  },
});

export const postGridColumn = styleVariants({
  multipleColumns: [xAxisDivider],
  singleColumn: [yAxisDivider],
});
